import React from 'react'
import { Box, Grid, Button, TextField, Autocomplete, Typography, FormControl, InputLabel, Select, MenuItem, Paper, CircularProgress } from '@mui/material'
import { LocationOn, OpenInNew, VisibilityOutlined } from '@mui/icons-material'
import { getRequest, postRequest, putRequest } from 'Includes/api/apicall'
import { GET_URL, POST_URL, PUT_URL } from 'Includes/urls'
import { Link, useNavigate } from 'react-router-dom'

const getEmptyForm = () => ({
  primary_contact_name: '',
  primary_contact_phone_number: '',
  employee1_name: '',
  employee1_phone_number: '',
  billing_address: '',
  map_location: '',
  agreement: null,
  client_billing_status: 'GOOD',
})

function CompanySecondaryDetails(props) {
  const isEdit = Boolean(props?.location?.state?.detail)
  const detailId = props?.location?.state?.detail
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [companies, setCompanies] = React.useState([])
  const [selectedCompany, setSelectedCompany] = React.useState(null)
  const [agreementFile, setAgreementFile] = React.useState(null)
  const [existingAgreementUrl, setExistingAgreementUrl] = React.useState('')
  const [errors, setErrors] = React.useState({})

  const [form, setForm] = React.useState(() => getEmptyForm())

  const loadCompanies = () => {
    getRequest(GET_URL.company.api, {}, props).then((response) => {
      const data = response?.data?.data || []
      setCompanies(Array.isArray(data) ? data : [])
    })
  }

  const loadDetail = (id) => {
    if (!id) return
    setLoading(true)
    getRequest(`${GET_URL.companysecondarydetails.api}${id}/`, {}, props)
      .then((response) => {
        const d = response?.data?.data || response?.data
        if (!d) return
        setForm({
          primary_contact_name: d.primary_contact_name || '',
          primary_contact_phone_number: d.primary_contact_phone_number || '',
          employee1_name: d.employee1_name || '',
          employee1_phone_number: d.employee1_phone_number || '',
          billing_address: d.billing_address || '',
          map_location: d.map_location || '',
          agreement: d.agreement || null,
          client_billing_status: d.client_billing_status || 'GOOD',
        })
        setExistingAgreementUrl(d?.agreement_details?.file || '')
        // company selection from list
        const c = companies.find(x => String(x.id) === String(d.company))
        if (c) setSelectedCompany(c)
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    loadCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (isEdit && companies.length > 0) {
      loadDetail(detailId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies])

  // Extract coordinates from input (supports "lat,lng" or Google Maps URL)
  const extractCoordinates = (input) => {
    if (!input || !input.trim()) return null
    
    const trimmed = input.trim()
    
    // Check if it's in "lat,lng" format
    if (/^-?\d+\.?\d*,-?\d+\.?\d*$/.test(trimmed)) {
      const parts = trimmed.split(',')
      return {
        lat: parseFloat(parts[0].trim()),
        lng: parseFloat(parts[1].trim())
      }
    }
    
    // Check if it's a Google Maps URL containing exact pin coordinates !3d...!4d...
    const placeMatch = trimmed.match(/!3d([+-]?\d+\.?\d*)!4d([+-]?\d+\.?\d*)/)
    if (placeMatch) {
      return {
        lat: parseFloat(placeMatch[1]),
        lng: parseFloat(placeMatch[2])
      }
    }
    
    // Check if it's a Google Maps URL with q=lat,lng
    const urlMatch = trimmed.match(/[?&]q=([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/)
    if (urlMatch) {
      return {
        lat: parseFloat(urlMatch[1]),
        lng: parseFloat(urlMatch[2])
      }
    }
    
    // Check if it's a Google Maps URL with @lat,lng
    const atMatch = trimmed.match(/@([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/)
    if (atMatch) {
      return {
        lat: parseFloat(atMatch[1]),
        lng: parseFloat(atMatch[2])
      }
    }

    return null
  }

  // Open Google Maps with coordinates
  const openGoogleMaps = () => {
    const coords = extractCoordinates(form.map_location)
    if (coords) {
      const url = `https://www.google.com/maps?q=${coords.lat},${coords.lng}`
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const validate = () => {
    const nextErrors = {}
    if (!selectedCompany?.id) nextErrors.company = 'Company is required'
    if (!String(form.primary_contact_name || '').trim()) nextErrors.primary_contact_name = 'Required'
    if (!String(form.primary_contact_phone_number || '').trim()) {
      nextErrors.primary_contact_phone_number = 'Required'
    } else if (form.primary_contact_phone_number.length > 13) {
      nextErrors.primary_contact_phone_number = 'Phone number must be maximum 13 digits'
    } else if (!/^\d+$/.test(form.primary_contact_phone_number)) {
      nextErrors.primary_contact_phone_number = 'Phone number must contain only numbers'
    }
    if (!String(form.employee1_name || '').trim()) nextErrors.employee1_name = 'Required'
    if (!String(form.employee1_phone_number || '').trim()) {
      nextErrors.employee1_phone_number = 'Required'
    } else if (form.employee1_phone_number.length > 13) {
      nextErrors.employee1_phone_number = 'Phone number must be maximum 13 digits'
    } else if (!/^\d+$/.test(form.employee1_phone_number)) {
      nextErrors.employee1_phone_number = 'Phone number must contain only numbers'
    }
    if (!String(form.billing_address || '').trim()) nextErrors.billing_address = 'Required'
    if (!String(form.map_location || '').trim()) {
      nextErrors.map_location = 'Required'
    } else {
      const trimmed = form.map_location.trim()
      if (trimmed.includes('maps.app.goo.gl') || trimmed.includes('goo.gl/maps')) {
        nextErrors.map_location = 'Shortened Google Maps links (maps.app.goo.gl) are not supported. Please paste the long Google Maps URL from your browser address bar or enter the coordinates directly.'
      } else {
        // Validate coordinates format
        const coords = extractCoordinates(form.map_location)
        if (!coords) {
          nextErrors.map_location = 'Invalid format. Use: lat,lng (e.g., 12.9716,77.5946) or paste Google Maps URL'
        } else if (isNaN(coords.lat) || isNaN(coords.lng)) {
          nextErrors.map_location = 'Invalid coordinates. Latitude and longitude must be valid numbers'
        } else if (coords.lat < -90 || coords.lat > 90) {
          nextErrors.map_location = 'Invalid latitude. Must be between -90 and 90'
        } else if (coords.lng < -180 || coords.lng > 180) {
          nextErrors.map_location = 'Invalid longitude. Must be between -180 and 180'
        }
      }
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload = new FormData()
      payload.append('company', selectedCompany.id)
      payload.append('primary_contact_name', form.primary_contact_name)
      payload.append('primary_contact_phone_number', form.primary_contact_phone_number)
      payload.append('employee1_name', form.employee1_name)
      payload.append('employee1_phone_number', form.employee1_phone_number)
      payload.append('billing_address', form.billing_address)
      payload.append('map_location', form.map_location)
      payload.append('client_billing_status', form.client_billing_status)
      if (agreementFile) {
        payload.append('agreement', agreementFile)
      }
      const reqProps = { ...props, contentType: 'multipart/form-data' }
      if (isEdit) {
        await putRequest(`${PUT_URL.companysecondarydetails.api}${detailId}/`, payload, reqProps)
      } else {
        await postRequest(POST_URL.companysecondarydetails.api, payload, reqProps)
      }
      navigate('/companysecondarydetails/list')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setErrors({})
    setAgreementFile(null)
    if (isEdit && detailId) {
      loadDetail(detailId)
    } else {
      setForm(getEmptyForm())
      setSelectedCompany(null)
      setExistingAgreementUrl('')
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Company billing details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? 'Update contacts, billing address, map location, and agreement.'
              : 'Create and configure billing and contact information for a company.'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/companysecondarydetails/list"
            startIcon={<VisibilityOutlined />}
            sx={{
              mt: { xs: 2, md: 0 },
              borderRadius: 2,
              textTransform: 'none',
              px: 2,
              py: 1,
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
            }}
          >
            Company Billing details
          </Button>
        </Grid>
      </Grid>

      <Paper
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          maxWidth: 960,
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Autocomplete
                options={companies}
                getOptionLabel={(o) => o?.name || ''}
                value={selectedCompany}
                onChange={(_e, v) => { setSelectedCompany(v); setErrors(prev => ({ ...prev, company: undefined })) }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company *"
                    error={Boolean(errors.company)}
                    helperText={errors.company || ''}
                  />
                )}
                fullWidth
                disableClearable={false}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                label="Primary Contact Name"
                value={form.primary_contact_name}
                onChange={(e) => setForm(prev => ({ ...prev, primary_contact_name: e.target.value }))}
                fullWidth
                required
                error={Boolean(errors.primary_contact_name)}
                helperText={errors.primary_contact_name || ''}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                label="Primary Contact Phone"
                required
                value={form.primary_contact_phone_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 13);
                  setForm(prev => ({ ...prev, primary_contact_phone_number: value }));
                  if (errors.primary_contact_phone_number) {
                    setErrors(prev => ({ ...prev, primary_contact_phone_number: undefined }));
                  }
                }}
                fullWidth
                inputProps={{ maxLength: 13 }}
                error={Boolean(errors.primary_contact_phone_number)}
                helperText={errors.primary_contact_phone_number || ''}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                label="Employee Name"
                required
                value={form.employee1_name}
                onChange={(e) => setForm(prev => ({ ...prev, employee1_name: e.target.value }))}
                fullWidth
                error={Boolean(errors.employee1_name)}
                helperText={errors.employee1_name || ''}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                label="Employee Phone"
                required
                value={form.employee1_phone_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 13);
                  setForm(prev => ({ ...prev, employee1_phone_number: value }));
                  if (errors.employee1_phone_number) {
                    setErrors(prev => ({ ...prev, employee1_phone_number: undefined }));
                  }
                }}
                fullWidth
                inputProps={{ maxLength: 13 }}
                error={Boolean(errors.employee1_phone_number)}
                helperText={errors.employee1_phone_number || ''}
              />
            </Grid>
            <Grid item md={12} xs={12}>
              <TextField
                label="Billing Address"
                required
                value={form.billing_address}
                onChange={(e) => setForm(prev => ({ ...prev, billing_address: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
                error={Boolean(errors.billing_address)}
                helperText={errors.billing_address || ''}
              />
            </Grid>
            <Grid item md={12} xs={12}>
              <TextField
                label="Map Location"
                value={form.map_location}
                onChange={(e) => {
                  setForm(prev => ({ ...prev, map_location: e.target.value }))
                  if (errors.map_location) {
                    setErrors(prev => ({ ...prev, map_location: undefined }))
                  }
                }}
                required
                fullWidth
                multiline
                minRows={2}
                error={Boolean(errors.map_location)}
                helperText={
                  errors.map_location || 
                  'Enter coordinates as: lat,lng (e.g., 12.9716,77.5946) or paste Google Maps URL'
                }
              />
              {form.map_location && extractCoordinates(form.map_location) && (
                <Box mt={1} display="flex" alignItems="center" gap={1}>
                  <LocationOn fontSize="small" color="primary" />
                  <Typography
                    variant="body2"
                    component="a"
                    href={`https://www.google.com/maps?q=${extractCoordinates(form.map_location).lat},${extractCoordinates(form.map_location).lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        color: 'primary.dark',
                      },
                    }}
                  >
                    Click to open location in Google Maps
                    <OpenInNew fontSize="small" />
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel>Client Billing Status</InputLabel>
                <Select
                  value={form.client_billing_status}
                  onChange={(e) => setForm(prev => ({ ...prev, client_billing_status: e.target.value }))}
                  label="Client Billing Status"
                >
                  <MenuItem value="GOOD">GOOD</MenuItem>
                  <MenuItem value="MODERATE">MODERATE</MenuItem>
                  <MenuItem value="CRITICAL">CRITICAL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={12} xs={12}>
              <Typography variant="caption" color="textSecondary">
                Agreement: upload a file (stored as Document).
              </Typography>
              <input
                type="file"
                onChange={(e) => {
                  setAgreementFile(e.target.files?.[0] || null)
                  setErrors(prev => ({ ...prev, agreement: undefined }))
                }}
              />
              {Boolean(errors.agreement) ? (
                <Box mt={1}>
                  <Typography variant="caption" color="error">{errors.agreement}</Typography>
                </Box>
              ) : null}
              {(isEdit && form.agreement) ? (
                <Box mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    Existing Agreement: {existingAgreementUrl ? <a href={existingAgreementUrl} target="_blank" rel="noreferrer">Download</a> : `Document ID ${form.agreement}`}
                  </Typography>
                </Box>
              ) : null}
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            <Button variant="outlined" onClick={handleReset} sx={{ textTransform: 'none' }}>
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Submit')}
            </Button>
          </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default CompanySecondaryDetails;


